import { Component } from '@theme/component';
import { onDocumentReady, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */


/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];


  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Breakpoints for tracking viewport changes
   * @constant {number[]}
   */
  #breakpoints = [750, 1024];

  /**
   * Mobile breakpoint width (matches the theme's mobile breakpoint)
   * @constant {number}
   */
  #mobileBreakpoint = 750;

  /**
   * Track current breakpoint range
   * @type {string}
   */
  #currentBreakpointRange = this.#getBreakpointRange(window.innerWidth);

  /**
   * Resize timeout for debouncing
   * @type {number | null}
   */
  #resizeTimeout = null;

  /**
   * Store initial menu visibility state to revert changes on resize
   * @type {{ menuHidden: boolean, drawerHidden: boolean } | null}
   */
  #initialMenuState = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };


  /**
   * Get the current breakpoint range based on window width
   * @param {number} width - The window width
   * @returns {string} The breakpoint range identifier
   */
  #getBreakpointRange(width) {
    if (width < this.#breakpoints[0]) return 'mobile'; // < 750px
    if (width < this.#breakpoints[1]) return 'tablet'; // 750-1023px
    return 'desktop'; // >= 1024px
  }

  /**
   * Capture the initial menu state for reverting changes on resize
   */
  #captureInitialState() {
    if (this.#initialMenuState) return;

    this.#initialMenuState = {
      menuHidden: this.refs.headerMenu.classList.contains('hidden'),
      drawerHidden: this.refs.headerDrawerContainer.classList.contains('desktop:hidden'),
    };
  }

  /**
   * Revert menu state to initial state
   */
  #revertToInitialState() {
    if (!this.#initialMenuState) return;

    // Reset menu visibility to initial state
    if (this.#initialMenuState.menuHidden) {
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerMenu.classList.remove('hidden');
    }

    // Reset drawer visibility to initial state
    if (this.#initialMenuState.drawerHidden) {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
    } else {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
    }
  }

  /**
   * Handles window resize by reverting resize-induced changes
   * This ensures header state is properly restored when crossing breakpoints
   */
  #handleWindowResize = () => {
    // Clear existing timeout
    if (this.#resizeTimeout) {
      clearTimeout(this.#resizeTimeout);
    }

    // Debounce the resize handler to avoid processing during active resizing
    this.#resizeTimeout = setTimeout(() => {
      const previousRange = this.#currentBreakpointRange;
      const currentRange = this.#getBreakpointRange(window.innerWidth);

      // Check if we've crossed into a different breakpoint range
      if (previousRange !== currentRange) {
        // Revert any resize-induced changes back to initial state
        this.#revertToInitialState();

        // Update the current breakpoint range
        this.#currentBreakpointRange = currentRange;

        // Re-capture initial state for the new breakpoint
        this.#captureInitialState();
      }
    }, 250);
  };

  #handleWindowScroll = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isScrollingUp) {
        if (this.getBoundingClientRect().top >= 0) {
          this.dataset.scrollDirection = 'none';
        } else {
          this.dataset.scrollDirection = 'up';
        }
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (this.getBoundingClientRect().top >= 0) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);

    // Capture initial menu state before any resize events
    this.#captureInitialState();

    // Add window resize listener to handle menu visibility on viewport changes
    window.addEventListener('resize', this.#handleWindowResize);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    window.removeEventListener('resize', this.#handleWindowResize);
    document.removeEventListener('scroll', this.#handleWindowScroll);

    // Clear any pending timeouts
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }
    if (this.#resizeTimeout) {
      clearTimeout(this.#resizeTimeout);
      this.#resizeTimeout = null;
    }

    // Clear stored state
    this.#initialMenuState = null;

    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentReady(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});

/**
 * Calculate and set the header group height
 * @param {HTMLElement | null} header
 * @param {HTMLElement} headerGroup
 */
function calculateHeaderGroupHeight(header, headerGroup) {
  if (!header) {
    document.body.style.setProperty('--header-group-height', '0px');
    return;
  }

  let height = header.getBoundingClientRect().height;

  // Add heights of other elements in header group
  const children = headerGroup.children;
  for (let i = 0; i < children.length; i++) {
    const element = children[i];
    if (element === header || !(element instanceof HTMLElement)) continue;
    height += element.getBoundingClientRect().height;
  }

  document.body.style.setProperty('--header-group-height', `${height}px`);
}
